// G-C5: literales en euskera (default) y castellano.
export type Lang = 'eu' | 'es'

export const ESTADOS_F11 = ['1', 'EM', 'RC', 'VA', 'LS', 'EN', 'TR', 'EX', 'OT', 'NJ'] as const
export type EstadoF11 = (typeof ESTADOS_F11)[number]

// Presencias que aporta cada estado — mismo peso que el API (`PESO_NUMERADOR.f11` en
// apps/api/src/asistencia/estados.ts). Issue #73: Recupera entrenamiento cuenta como 2 presencias.
export const PRESENCIAS_F11: Partial<Record<string, number>> = { '1': 1, EM: 1, RC: 2 }

export function presenciasF11(registros: { estado: string | null }[]): number {
  return registros.reduce((acc, r) => acc + (PRESENCIAS_F11[r.estado ?? ''] ?? 0), 0)
}

export const ESTADO_LABEL: Record<EstadoF11, { eu: string; es: string }> = {
  '1': { eu: 'Bertaratua', es: 'Asiste' },
  EM: { eu: 'Bazterrean', es: 'Al margen' },
  RC: { eu: 'Berreskuratzen', es: 'Recupera' },
  VA: { eu: 'Oporrak', es: 'Vacaciones' },
  LS: { eu: 'Lesioa', es: 'Lesión' },
  EN: { eu: 'Gaixo', es: 'Enfermo' },
  TR: { eu: 'Lana', es: 'Trabajo' },
  EX: { eu: 'Ikasketa', es: 'Estudio' },
  OT: { eu: 'Beste', es: 'Otros' },
  NJ: { eu: 'Justif. gabe', es: 'No justif.' },
}

const DICT = {
  markarSaioa: { eu: 'SAIOA MARKATU', es: 'MARCAR SESIÓN' },
  sinMarcar: { eu: 'Markatu gabe', es: 'Sin marcar' },
  porcAno: { eu: 'Urtekoa', es: 'Anual' },
  porcMes: { eu: 'Hilabetekoa', es: 'Mensual' },
  jugador: { eu: 'Jokalaria', es: 'Jugador' },
  total: { eu: 'GUZTIRA', es: 'TOTAL' },
  totalGeneral: { eu: 'GUZTIZKO GUZTIRA', es: 'TOTAL GENERAL' },
  media: { eu: 'Batez besteko', es: 'Media' },
  conFicha: { eu: 'Fitxadunak', es: 'Con Ficha' },
  sinFicha: { eu: 'Fitxarik gabekoak', es: 'Sin Ficha' },
  entrenadores: { eu: 'Entrenatzaileak', es: 'Entrenadores' },
  equipo: { eu: 'Taldea', es: 'Equipo' },
  mes: { eu: 'Hila', es: 'Mes' },
  sesiones: { eu: 'Saioak', es: 'Sesiones' },
  totalTemporada: { eu: 'Denboraldiko guztira', es: 'Total temp.' },
  asistencias: { eu: 'Asistentziak', es: 'Asistencias' },
  mediaGeneral: { eu: 'Batez bestekoa (%)', es: 'Media general %' },
  mediaFicha: { eu: 'Fitxa batez bestekoa (%)', es: 'Media ficha %' },
  sinAcceso: { eu: 'Sarbiderik ez', es: 'Sin acceso' },
  sinAccesoAsistenciaMensaje: {
    eu: 'Ez duzu talde honen asistentziara sarbiderik.',
    es: 'No tienes acceso a la asistencia de este equipo.',
  },
  errorGenerico: { eu: 'Errore bat gertatu da', es: 'Ha ocurrido un error' },
  temporadaCerrada: {
    eu: 'Denboraldia itxita dago; datuak irakurtzeko soilik dira.',
    es: 'La temporada está cerrada; los datos son de solo lectura.',
  },
  quitarDia: { eu: 'Entrenamendurik ez', es: 'No hay entrenamiento' },
  quitarDiaConfirm: { eu: 'Entrenamendurik ez egun honetan? Ez du kontatuko.', es: '¿No hay entrenamiento este día? No contará en los porcentajes.' },
  editarDias: { eu: 'Editatu', es: 'Editar' },
  guardarDias: { eu: 'Gorde', es: 'Guardar' },
  leyenda: { eu: 'Legenda', es: 'Leyenda' },
  leyendaExplicacionComputo: {
    eu: '1 (Bertaratua), EM (Bazterrean) eta RC (Berreskuratzen) bertaratze gisa kontatzen dira. Gainerako egoerek (oporrak, lesioa, gaixo...) ez dute ez onik ez kalterik egiten %-an. Hutsik = markatu gabe, ez du kontatzen.',
    es: '1 (Asiste), EM (Al margen) y RC (Recupera) computan como asistencia. El resto de estados (vacaciones, lesión, enfermo...) no suman ni restan al %. Vacío = sin marcar, no cuenta.',
  },
  resumenTemporada: { eu: 'Denboraldiko laburpena', es: 'Resumen temporada' },
  evolucionMensual: { eu: 'Hileroko bilakaera', es: 'Evolución mensual' },
  exportarCsv: { eu: '⬇ CSV', es: '⬇ CSV' },
  marcarTodos: { eu: 'Guztiak markatuta', es: 'Todos marcados' },
  // Issue #32: desplegable de 3 opciones por día, sustituye al botón "✓ marcar todos" suelto.
  estadoDiaPlaceholder: { eu: 'Eguneko ekintza...', es: 'Acción del día…' },
} as const

export type DictKey = keyof typeof DICT


export function t(key: DictKey, lang: Lang): string {
  return DICT[key][lang]
}

export function estadoLabel(estado: EstadoF11, lang: Lang): string {
  return ESTADO_LABEL[estado]?.[lang] ?? estado
}

export function umbralF11(porcentaje: number | '--'): 'verde' | 'ambar' | 'rojo' | 'gris' {
  if (porcentaje === '--') return 'gris'
  if (porcentaje >= 85) return 'verde'
  if (porcentaje >= 60) return 'ambar'
  if (porcentaje > 0) return 'rojo'
  return 'gris'
}

export const UMBRAL_COLOR: Record<ReturnType<typeof umbralF11>, string> = {
  verde: 'var(--color-success)',
  ambar: 'var(--color-warning)',
  rojo: 'var(--color-danger)',
  gris: 'var(--color-neutral)',
}
